import Decimal from "decimal.js";
import { roundCurrency } from "@/lib/utils/math";

export type SolveMode = "time" | "monthly";

export type CompoundingFrequency = "monthly" | "quarterly" | "annual";

export interface SavingsGoalInput {
  /** Target amount to reach (in today's dollars when inflationAdjusted) */
  targetAmount: number;
  /** Current savings */
  currentSavings: number;
  /** Monthly contribution (input when solveMode is "time") */
  monthlyContribution: number;
  /** Expected annual return (percent) */
  expectedReturn: number;
  /** Time horizon in years (input when solveMode is "monthly") */
  years: number;
  /** What to solve for: time to goal or required monthly */
  solveMode: SolveMode;
  /** Adjust target for inflation (target in today's $, we solve for nominal future $) */
  inflationAdjusted?: boolean;
  /** Annual inflation rate (percent) when inflationAdjusted */
  inflationRate?: number;
  /** How often interest compounds */
  compoundingFrequency?: CompoundingFrequency;
}

export interface SavingsGoalResult {
  /** Projected balance at end of time horizon */
  projectedBalance: number;
  /** Whether the goal will be reached */
  goalReached: boolean;
  /** Shortfall (positive) or surplus (negative) vs target */
  shortfallOrSurplus: number;
  /** Required monthly contribution (computed when solveMode is "monthly") */
  requiredMonthlyContribution: number | null;
  /** Years to reach goal (computed when solveMode is "time") */
  yearsToGoal: number | null;
  /** Months to reach goal (computed when solveMode is "time") */
  monthsToGoal: number | null;
  /** Total contributions over the period */
  totalContributions: number;
  /** Investment growth over the period */
  totalGrowth: number;
  /** Effective years used (input years or computed years) */
  effectiveYears: number;
  /** Nominal target (inflation-adjusted when applicable) */
  nominalTarget: number;
  chartData: { year: number; balance: number }[];
}

/** Future value with monthly contributions. freq: compounding periods per year (12=monthly, 4=quarterly, 1=annual) */
function futureValueWithFreq(
  pv: Decimal,
  pmt: Decimal,
  r: Decimal,
  t: Decimal,
  freq: number
): Decimal {
  const periodsPerYear = freq;
  const ratePerPeriod = r.div(periodsPerYear);
  const totalPeriods = t.mul(periodsPerYear);
  const contributionsPerPeriod = 12 / periodsPerYear; // 12 monthly payments per period
  const pmtPerPeriod = pmt.mul(contributionsPerPeriod);

  const growthFactor = new Decimal(1).add(ratePerPeriod).pow(totalPeriods);

  if (ratePerPeriod.eq(0)) {
    return pv.add(pmt.mul(12).mul(t));
  }

  const annuityFactor = growthFactor.minus(1).div(ratePerPeriod);
  return pv.mul(growthFactor).add(pmtPerPeriod.mul(annuityFactor));
}

function futureValue(
  pv: Decimal,
  pmt: Decimal,
  r: Decimal,
  t: Decimal,
  freq: CompoundingFrequency = "monthly"
): Decimal {
  const n = freq === "monthly" ? 12 : freq === "quarterly" ? 4 : 1;
  return futureValueWithFreq(pv, pmt, r, t, n);
}

/** Solve for PMT */
function solveRequiredMonthly(
  pv: Decimal,
  fv: Decimal,
  r: Decimal,
  t: Decimal,
  freq: CompoundingFrequency = "monthly"
): Decimal {
  const n = freq === "monthly" ? 12 : freq === "quarterly" ? 4 : 1;
  const ratePerPeriod = r.div(n);
  const totalPeriods = t.mul(n);
  const contributionsPerPeriod = 12 / n;
  const growthFactor = new Decimal(1).add(ratePerPeriod).pow(totalPeriods);

  if (ratePerPeriod.eq(0)) {
    return fv.minus(pv).div(12 * t.toNumber());
  }

  const annuityFactor = growthFactor.minus(1).div(ratePerPeriod);
  return fv.minus(pv.mul(growthFactor)).div(annuityFactor.mul(contributionsPerPeriod));
}

/** Binary search for years to reach goal */
function yearsToReachGoal(
  pv: Decimal,
  pmt: Decimal,
  fv: Decimal,
  r: Decimal,
  freq: CompoundingFrequency = "monthly"
): number {
  let low = 0;
  let high = 100;
  for (let i = 0; i < 50; i++) {
    const mid = (low + high) / 2;
    const fvAtMid = futureValue(pv, pmt, r, new Decimal(mid), freq);
    if (fvAtMid.gte(fv)) {
      high = mid;
    } else {
      low = mid;
    }
  }
  return (low + high) / 2;
}

export function calculateSavingsGoal(
  input: SavingsGoalInput
): SavingsGoalResult {
  const {
    targetAmount,
    currentSavings,
    monthlyContribution,
    expectedReturn,
    years,
    solveMode,
    inflationAdjusted = false,
    inflationRate = 3,
    compoundingFrequency = "monthly",
  } = input;

  const pv = new Decimal(currentSavings);
  const r = new Decimal(expectedReturn).div(100);
  const inf = new Decimal(inflationRate).div(100);

  let effectiveYears: number;
  let pmt: Decimal;
  let requiredMonthlyContribution: number | null = null;
  let yearsToGoal: number | null = null;
  let monthsToGoal: number | null = null;
  let nominalTarget: number;

  if (solveMode === "monthly") {
    effectiveYears = years;
    nominalTarget = inflationAdjusted
      ? roundCurrency(
          new Decimal(targetAmount).mul(
            new Decimal(1).add(inf).pow(effectiveYears)
          )
        )
      : targetAmount;
    const fvTarget = new Decimal(nominalTarget);
    const t = new Decimal(years);
    const reqPmt = solveRequiredMonthly(pv, fvTarget, r, t, compoundingFrequency);
    pmt = reqPmt;
    requiredMonthlyContribution = Math.max(0, roundCurrency(reqPmt));
  } else {
    pmt = new Decimal(monthlyContribution);
    if (inflationAdjusted) {
      // Solve for t where FV >= target * (1+inf)^t
      let yearsNeeded = 100;
      for (let y = 0; y <= 100; y += 0.0833) {
        const fv = futureValue(pv, pmt, r, new Decimal(y), compoundingFrequency);
        const nominal = new Decimal(targetAmount).mul(
          new Decimal(1).add(inf).pow(y)
        );
        if (fv.gte(nominal)) {
          yearsNeeded = y;
          break;
        }
      }
      yearsToGoal = Math.round(yearsNeeded * 10) / 10;
      monthsToGoal = Math.round(yearsNeeded * 12);
      effectiveYears = Math.min(Math.ceil(yearsNeeded) + 1, 50);
      nominalTarget = roundCurrency(
        new Decimal(targetAmount).mul(
          new Decimal(1).add(inf).pow(yearsNeeded)
        )
      );
    } else {
      const fvTarget = new Decimal(targetAmount);
      const yearsNeeded = yearsToReachGoal(pv, pmt, fvTarget, r, compoundingFrequency);
      yearsToGoal = Math.round(yearsNeeded * 10) / 10;
      monthsToGoal = Math.round(yearsNeeded * 12);
      effectiveYears = Math.min(Math.ceil(yearsNeeded) + 1, 50);
      nominalTarget = targetAmount;
    }
  }

  const fvTarget = new Decimal(nominalTarget);
  const t = new Decimal(effectiveYears);
  const projectedDecimal = futureValue(pv, pmt, r, t, compoundingFrequency);
  const projectedBalance = roundCurrency(projectedDecimal);
  const goalReached = projectedDecimal.gte(fvTarget);
  const shortfallOrSurplus = roundCurrency(
    projectedDecimal.minus(fvTarget)
  );

  const totalContributions = roundCurrency(pmt.mul(12).mul(effectiveYears));
  const totalGrowth = roundCurrency(
    projectedDecimal.minus(currentSavings).minus(totalContributions)
  );

  const chartData: { year: number; balance: number }[] = [];
  const chartYears = solveMode === "time" && yearsToGoal != null
    ? Math.ceil(yearsToGoal) + 1
    : effectiveYears;
  for (let y = 0; y <= chartYears; y++) {
    const yearT = new Decimal(y);
    const yearBalance = futureValue(pv, pmt, r, yearT, compoundingFrequency);
    chartData.push({
      year: y,
      balance: roundCurrency(yearBalance),
    });
  }

  return {
    projectedBalance,
    goalReached,
    shortfallOrSurplus,
    requiredMonthlyContribution,
    yearsToGoal,
    monthsToGoal,
    totalContributions,
    totalGrowth,
    effectiveYears,
    nominalTarget,
    chartData,
  };
}
